import PropTypes from 'prop-types';
import { Breadcrumbs, Link, Typography } from '@material-ui/core';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';

Breadcrumb.propTypes = {
  labels: PropTypes.object,
  homeLabel: PropTypes.string,
  homePath: PropTypes.string,
  // Path segments to omit from the trail. Useful when a route includes a
  // fixed delimiter word (e.g. `/team-usage/:team/part/:part`) where the
  // delimiter segment is not an entity in its own right and shouldn't be a
  // clickable crumb.
  exclude: PropTypes.arrayOf(PropTypes.string),
  sx: PropTypes.object
};

Breadcrumb.defaultProps = {
  labels: {},
  homeLabel: 'Home',
  homePath: '/racks',
  exclude: [],
  sx: {}
};

// Split a path segment like "my-team" or "cmp_001" into "My Team" / "cmp 001".
// IDs (anything containing a digit) are kept verbatim so they remain unique.
function humanize(segment) {
  if (/\d/.test(segment)) return segment;
  return segment
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function Breadcrumb({ labels, homeLabel, homePath, exclude, sx }) {
  const location = useLocation();
  const params = useParams();

  const segments = location.pathname.split('/').filter(Boolean);
  // Track the *original* index alongside each kept segment so the crumb `to`
  // always reflects the actual URL, and so `isLast` is computed against the
  // post-filter trail (otherwise a trailing excluded segment would leave no
  // terminal crumb and the last visible one would incorrectly render as a
  // clickable Link).
  const crumbs = segments
    .map((segment, index) => ({ segment, originalIndex: index }))
    .filter(({ segment }) => !exclude.includes(segment))
    .map(({ segment, originalIndex }, index, arr) => {
      const to = `/${segments.slice(0, originalIndex + 1).join('/')}`;
      const isLast = index === arr.length - 1;

      let label;
      if (labels[segment]) {
        label = labels[segment];
      } else if (labels[to]) {
        label = labels[to];
      } else {
        // If this segment is a route param, prefer the resolved param value.
        const resolvedParam = Object.entries(params).find(([, value]) => value === segment);
        label = resolvedParam ? segment : humanize(segment);
      }

      return { to, label, isLast };
    });

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={sx}>
      <Link component={RouterLink} to={homePath} color="inherit">
        {homeLabel}
      </Link>
      {crumbs.map(({ to, label, isLast }) =>
        isLast ? (
          <Typography key={to} color="text.primary">
            {label}
          </Typography>
        ) : (
          <Link key={to} component={RouterLink} to={to} color="inherit">
            {label}
          </Link>
        )
      )}
    </Breadcrumbs>
  );
}
